import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon,
  GlobalSearchIcon,
  BookOpen01Icon,
  InternetIcon,
  News01Icon,
  Calculator01Icon,
  CommandLineIcon,
  Clock01Icon,
  YoutubeIcon,
  Csv01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Tick01Icon,
  Cancel01Icon,
  PlusSignIcon,
} from "@hugeicons/core-free-icons";
import type { IconSvgElement } from "@hugeicons/react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  PREDEFINED_AGENTS,
  TOOL_GROUPS,
  TOOL_DISPLAY_NAMES,
} from "@/lib/data/predefined-agents";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import type { ModelParam } from "@/lib/types/model-catalog";
import type { PipelineNode } from "@/lib/types/pipeline";
import { cn } from "@/lib/utils";

const TOOL_ICONS: Record<string, IconSvgElement> = {
  duckduckgo: Search01Icon,
  tavily: GlobalSearchIcon,
  wikipedia: BookOpen01Icon,
  jina_reader: InternetIcon,
  newspaper4k: News01Icon,
  calculator: Calculator01Icon,
  python: CommandLineIcon,
  sleep: Clock01Icon,
  serpapi_youtube: YoutubeIcon,
  csv: Csv01Icon,
};

// Params shown in the Advanced section (not system_prompt or prompt)
const ADVANCED_PARAM_NAMES = new Set(["temperature", "max_tokens", "extended_thinking", "top_p", "top_k", "reasoning_effort"]);

interface AgentNodeSettingsProps {
  node: PipelineNode;
  onConfigChange: (key: string, value: unknown) => void;
}

function AgentParamField({
  param,
  value,
  onChange,
}: {
  param: ModelParam;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (
    (param.type === "float" || param.type === "integer") &&
    param.min !== undefined &&
    param.max !== undefined
  ) {
    const step = param.step ?? (param.type === "float" ? 0.1 : 1);
    const numValue = Number(value ?? param.default ?? param.min);
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">{param.label}</span>
          <span className="text-sm text-muted-foreground tabular-nums">{numValue}</span>
        </div>
        <Slider
          min={param.min}
          max={param.max}
          step={step}
          value={[numValue]}
          onValueChange={([v]) =>
            onChange(param.type === "integer" ? Math.round(v) : v)
          }
        />
        {param.description && (
          <p className="text-xs text-muted-foreground/60">{param.description}</p>
        )}
      </div>
    );
  }

  if (param.type === "boolean") {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">{param.label}</span>
          {param.description && (
            <p className="text-xs text-muted-foreground/60">{param.description}</p>
          )}
        </div>
        <Switch
          checked={Boolean(value ?? param.default ?? false)}
          onCheckedChange={(checked) => onChange(checked)}
        />
      </div>
    );
  }

  return null;
}

export function AgentNodeSettings({ node, onConfigChange }: AgentNodeSettingsProps) {
  const config = (node.data.config ?? {}) as Record<string, unknown>;
  const tools: string[] = (config.tools as string[]) ?? [];
  const maxIterations: number = (config.max_iterations as number) ?? 10;

  const [predefinedId, setPredefinedId] = useState<string>(
    (config.predefined_agent as string) ?? "custom",
  );
  const [templateOpen, setTemplateOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [addToolOpen, setAddToolOpen] = useState(false);

  const modelEntry = useModelCatalogStore((s) => {
    const modelId = config.model as string | undefined;
    return modelId ? s.getById(modelId) : undefined;
  });

  const allParams = modelEntry?.parameters_schema?.params ?? [];
  const systemPromptParam = allParams.find((p) => p.name === "system_prompt");
  const advancedParams = allParams.filter(
    (p) => p.name !== "prompt" && p.name !== "system_prompt" && ADVANCED_PARAM_NAMES.has(p.name),
  );

  const selectedTemplate = predefinedId !== "custom"
    ? PREDEFINED_AGENTS.find((a) => a.id === predefinedId)
    : null;

  function applyPredefined(id: string) {
    setPredefinedId(id);
    onConfigChange("predefined_agent", id);
    if (id === "custom") return;
    const template = PREDEFINED_AGENTS.find((a) => a.id === id);
    if (!template) return;
    onConfigChange("system_prompt", template.system_prompt);
    onConfigChange("tools", template.tools);
    onConfigChange("max_iterations", template.max_iterations);
  }

  function toggleTool(tool: string, enabled: boolean) {
    const next = enabled ? [...tools, tool] : tools.filter((t) => t !== tool);
    onConfigChange("tools", next);
    if (predefinedId !== "custom") {
      setPredefinedId("custom");
      onConfigChange("predefined_agent", "custom");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Separator />

      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Agent Settings
      </span>

      {/* Template picker — matches ModelCombobox style */}
      <div className="space-y-1.5">
        <Label>Template</Label>
        <Popover open={templateOpen} onOpenChange={setTemplateOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-10 w-full items-center justify-between px-3 text-sm bg-black/5 dark:bg-white/8 rounded-xl hover:bg-black/8 dark:hover:bg-white/10 outline-none transition-colors"
            >
              <span className={cn("truncate", selectedTemplate ? "text-foreground" : "text-muted-foreground/50")}>
                {selectedTemplate?.name ?? "Custom agent"}
              </span>
              <HugeiconsIcon icon={ArrowDown01Icon} className="size-3 shrink-0 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="p-0 w-(--radix-popover-trigger-width) min-w-[220px]"
          >
            <div className="py-1">
              <button
                type="button"
                className={cn(
                  "w-full px-3 py-2.5 text-left hover:bg-accent transition-colors",
                  predefinedId === "custom" && "bg-accent/50",
                )}
                onClick={() => { applyPredefined("custom"); setTemplateOpen(false); }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">Custom agent</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Configure from scratch</p>
                  </div>
                  {predefinedId === "custom" && (
                    <HugeiconsIcon icon={Tick01Icon} className="size-4 shrink-0 text-brand mt-0.5" />
                  )}
                </div>
              </button>
              <div className="mx-3 h-px bg-border/40" />
              {PREDEFINED_AGENTS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={cn(
                    "w-full px-3 py-2.5 text-left hover:bg-accent transition-colors",
                    predefinedId === a.id && "bg-accent/50",
                  )}
                  onClick={() => { applyPredefined(a.id); setTemplateOpen(false); }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{a.name}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground truncate">{a.description}</p>
                    </div>
                    {predefinedId === a.id && (
                      <HugeiconsIcon icon={Tick01Icon} className="size-4 shrink-0 text-brand mt-0.5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* System Prompt — from model params */}
      {systemPromptParam && (
        <div className="space-y-1.5">
          <Label>System Prompt</Label>
          <Textarea
            value={(config.system_prompt as string) ?? ""}
            onChange={(e) => onConfigChange("system_prompt", e.target.value)}
            placeholder="Describe the agent's role and goal…"
            className="min-h-[110px] resize-none font-sans text-sm"
          />
        </div>
      )}

      {/* Equipped Tools */}
      <div className="space-y-3">
        <Label>Tools</Label>
        
        {/* Selected Tools List */}
        {tools.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {tools.map((tool) => {
              const icon = TOOL_ICONS[tool];
              return (
                <div key={tool} className="flex items-center justify-between gap-2 rounded-lg border border-[oklch(0.55_0.25_285/0.15)] dark:border-white/10 bg-card px-3 py-2.5 text-sm shadow-sm">
                  <div className="flex items-center gap-2">
                    {icon && <HugeiconsIcon icon={icon} size={14} className="text-brand dark:text-[oklch(0.75_0.18_285)]" />}
                    <span className="font-medium text-[13px]">{TOOL_DISPLAY_NAMES[tool] ?? tool}</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleTool(tool, false); }}
                    className="p-1.5 hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-md transition-colors -mr-1"
                  >
                    <HugeiconsIcon icon={Cancel01Icon} size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border p-4 text-center">
            <p className="text-xs font-medium text-muted-foreground">No tools equipped</p>
          </div>
        )}

        {/* Add Tool Popover */}
        <Popover open={addToolOpen} onOpenChange={setAddToolOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="group flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-brand/40 hover:text-brand hover:bg-brand/5 mt-1"
            >
              <HugeiconsIcon icon={PlusSignIcon} size={14} className="transition-transform group-hover:scale-110" />
              Add Tool
            </button>
          </PopoverTrigger>
          <PopoverContent align="center" className="w-(--radix-popover-trigger-width) min-w-[260px] p-0 shadow-lg rounded-xl overflow-hidden" sideOffset={8}>
            <Command className="bg-popover border-none outline-none">
              <CommandInput placeholder="Search tools..." className="h-10 text-sm" />
              <CommandList className="max-h-[260px] p-1">
                <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">No tool found.</CommandEmpty>
                {TOOL_GROUPS.map((group) => {
                  const availableTools = group.tools.filter((t) => !tools.includes(t));
                  if (availableTools.length === 0) return null;
                  return (
                    <CommandGroup key={group.label} heading={group.label} className="text-[10px] font-medium text-muted-foreground">
                      {availableTools.map((tool) => {
                         const icon = TOOL_ICONS[tool];
                         return (
                           <CommandItem
                             key={tool}
                             value={TOOL_DISPLAY_NAMES[tool] ?? tool}
                             onSelect={() => {
                               toggleTool(tool, true);
                             }}
                             className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2 py-2 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground"
                           >
                             {icon && <HugeiconsIcon icon={icon} size={14} className="text-muted-foreground" />}
                             <span className="font-medium">{TOOL_DISPLAY_NAMES[tool] ?? tool}</span>
                           </CommandItem>
                         );
                      })}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Advanced collapsible — temperature, max tokens, extended thinking, max iterations */}
      <Collapsible
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        className="flex flex-col gap-1 rounded-xl border border-[oklch(0.55_0.25_285/0.15)] dark:border-white/10 bg-card p-3 shadow-sm mt-2"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full text-[10px] font-semibold text-muted-foreground hover:text-foreground uppercase tracking-wider group"
          >
            <span>Advanced Settings</span>
            <div className="flex size-6 items-center justify-center rounded-md bg-transparent group-hover:bg-accent transition-colors -mr-1">
              <HugeiconsIcon
                icon={advancedOpen ? ArrowUp01Icon : ArrowDown01Icon}
                size={14}
                className="opacity-70 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-3 overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          {advancedParams.map((param) => (
            <AgentParamField
              key={param.name}
              param={param}
              value={config[param.name]}
              onChange={(value) => onConfigChange(param.name, value)}
            />
          ))}

          {/* Max Iterations */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Max Iterations</span>
              <span className="text-sm text-muted-foreground tabular-nums">{maxIterations}</span>
            </div>
            <Slider
              min={1}
              max={20}
              step={1}
              value={[maxIterations]}
              onValueChange={([val]) => onConfigChange("max_iterations", val)}
            />
            <p className="text-xs text-muted-foreground/60">
              Safety cap on the agentic loop. Higher = more thorough but slower.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
